import React from 'react';
import { 
  Search, 
  Filter, 
  Calendar, 
  DollarSign,
  Clock,
  AlertTriangle,
  Users,
  Download,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
  CalendarDays,
  CalendarRange,
  FilterX,
  Settings,
  MoreHorizontal
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  Switch
} from '../ui/switch';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  OverdueFilters as OverdueFiltersType, 
  OverdueStatus, 
  OverduePriority,
  OverdueSummary,
  OVERDUE_STATUS_LABELS,
  OVERDUE_PRIORITY_LABELS
} from '../../types/overdue';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatCurrency } from '@/lib/utils/currencyUtils';

interface OverdueFiltersProps {
  filters: OverdueFiltersType;
  onFiltersChange: (filters: OverdueFiltersType) => void;
  summary: OverdueSummary;
  availableCategories?: Array<{ id: string; name: string }>;
  availableAccounts?: Array<{ id: string; name: string }>;
  onRefresh?: () => void;
  onExport?: () => void;
  loading?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function OverdueFilters({
  filters,
  onFiltersChange,
  summary,
  availableCategories = [],
  availableAccounts = [],
  onRefresh,
  onExport,
  loading = false
}: OverdueFiltersProps) {

  const handleStatusChange = (status: string) => {
    if (status === 'all') {
      onFiltersChange({
        ...filters,
        status: Object.values(OverdueStatus)
      });
    } else {
      onFiltersChange({
        ...filters,
        status: [status as OverdueStatus]
      });
    }
  };

  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...filters,
      client_search: value
    });
  };

  const handlePriorityFilter = (priorities: OverduePriority[]) => {
    onFiltersChange({
      ...filters,
      priority: priorities
    });
  };

  const handleDaysOverdueChange = (range: [number, number]) => {
    onFiltersChange({
      ...filters,
      days_overdue_min: range[0],
      days_overdue_max: range[1]
    });
  };

  const handleAmountRangeChange = (min?: number, max?: number) => {
    onFiltersChange({
      ...filters,
      amount_min: min,
      amount_max: max
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      status: Object.values(OverdueStatus),
      priority: Object.values(OverduePriority),
      sort_by: 'days_overdue',
      sort_direction: 'desc'
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.client_search) count++;
    if (filters.priority?.length !== Object.values(OverduePriority).length) count++;
    if (filters.days_overdue_min !== undefined || filters.days_overdue_max !== undefined) count++;
    if (filters.amount_min !== undefined || filters.amount_max !== undefined) count++;
    if (filters.categories?.length) count++;
    if (filters.payment_accounts?.length) count++;
    if (filters.last_contact_days) count++;
    return count;
  };

  const getCurrentStatusLabel = () => {
    if (filters.status.length === Object.values(OverdueStatus).length) {
      return 'Todos';
    }
    if (filters.status.length === 1) {
      return OVERDUE_STATUS_LABELS[filters.status[0]];
    }
    return `${filters.status.length} status`;
  };


  return (
    <div className="space-y-4">
      {/* Status Tabs */}
      <div className="flex items-center justify-between">
        <Tabs 
          value={filters.status.length === 1 ? filters.status[0] : 'all'} 
          onValueChange={handleStatusChange}
        >
          <TabsList>
            <TabsTrigger value="all" className="relative">
              Todos
              <Badge variant="secondary" className="ml-2 h-5 px-2 text-xs">
                {summary.total_transactions}
              </Badge>
            </TabsTrigger>
            {Object.entries(OVERDUE_STATUS_LABELS).map(([status, label]) => (
              <TabsTrigger key={status} value={status} className="relative">
                {label}
                <Badge variant="secondary" className="ml-2 h-5 px-2 text-xs">
                  {summary.by_status[status as OverdueStatus]?.count || 0}
                </Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRefresh}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          )}
          
          {onExport && (
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="h-4 w-4 mr-2" />
              Exportar
            </Button>
          )}
        </div>
      </div>

      {/* Main Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar pelo nome do cliente..."
            value={filters.client_search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Sort */}
        <Select 
          value={`${filters.sort_by}-${filters.sort_direction}`} 
          onValueChange={(value) => {
            const [sort_by, sort_direction] = value.split('-');
            onFiltersChange({
              ...filters,
              sort_by: sort_by as any,
              sort_direction: sort_direction as 'asc' | 'desc'
            });
          }}
        >
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="days_overdue-desc">Mais dias em atraso</SelectItem>
            <SelectItem value="days_overdue-asc">Menos dias em atraso</SelectItem>
            <SelectItem value="amount-desc">Maior valor</SelectItem>
            <SelectItem value="amount-asc">Menor valor</SelectItem>
            <SelectItem value="client_name-asc">Nome A-Z</SelectItem>
            <SelectItem value="client_name-desc">Nome Z-A</SelectItem>
            <SelectItem value="due_date-asc">Vencimento mais antigo</SelectItem>
            <SelectItem value="due_date-desc">Vencimento mais recente</SelectItem>
          </SelectContent>
        </Select>

        {/* Advanced Filters */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filtros
              {getActiveFiltersCount() > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 text-xs">
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel>Filtros Avançados</DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            {/* Priority Filter */}
            <div className="px-2 py-3">
              <Label className="text-sm font-medium mb-3 block">Prioridade</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(OVERDUE_PRIORITY_LABELS).map(([priority, label]) => (
                  <div key={priority} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`priority-${priority}`}
                      checked={filters.priority?.includes(priority as OverduePriority) || false}
                      onChange={(e) => {
                        const current = filters.priority || [];
                        if (e.target.checked) {
                          handlePriorityFilter([...current, priority as OverduePriority]);
                        } else {
                          handlePriorityFilter(current.filter(p => p !== priority));
                        }
                      }}
                      className="rounded border-border text-primary focus-internal"
                    />
                    <Label 
                      htmlFor={`priority-${priority}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <DropdownMenuSeparator />

            {/* Days Overdue Range */}
            <div className="px-2 py-3">
              <Label className="text-sm font-medium mb-3 block">Dias em Atraso</Label>
              <div className="space-y-3">
                <Slider
                  value={[filters.days_overdue_min || 0, filters.days_overdue_max || 365]}
                  onValueChange={handleDaysOverdueChange}
                  max={365}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{filters.days_overdue_min || 0} dias</span>
                  <span>{filters.days_overdue_max || 365} dias</span>
                </div>
              </div>
            </div>

            <DropdownMenuSeparator />

            {/* Amount Range */}
            <div className="px-2 py-3">
              <Label className="text-sm font-medium mb-3 block">Faixa de Valor</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Mínimo</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.amount_min || ''}
                    onChange={(e) => handleAmountRangeChange(
                      e.target.value ? Number(e.target.value) : undefined,
                      filters.amount_max
                    )}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Máximo</Label>
                  <Input
                    type="number"
                    placeholder="Sem limite"
                    value={filters.amount_max || ''}
                    onChange={(e) => handleAmountRangeChange(
                      filters.amount_min,
                      e.target.value ? Number(e.target.value) : undefined
                    )}
                    className="h-8"
                  />
                </div>
              </div>
            </div>

            {/* Categories */}
            {availableCategories.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-3">
                  <Label className="text-sm font-medium mb-3 block">Categorias</Label>
                  <div className="max-h-32 overflow-y-auto space-y-2">
                    {availableCategories.map(category => (
                      <div key={category.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`category-${category.id}`}
                          checked={filters.categories?.includes(category.id) || false}
                          onChange={(e) => {
                            const current = filters.categories || [];
                            onFiltersChange({
                              ...filters,
                              categories: e.target.checked
                                ? [...current, category.id]
                                : current.filter(id => id !== category.id)
                            });
                          }}
                          className="rounded border-border text-primary focus-internal"
                        />
                        <Label 
                          htmlFor={`category-${category.id}`}
                          className="text-sm font-normal cursor-pointer"
                        >
                          {category.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <DropdownMenuSeparator />
            
            <DropdownMenuItem onClick={clearFilters}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Limpar todos os filtros
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="flex items-center gap-3 p-4 border rounded-lg">
          <div className="w-10 h-10 rounded-full bg-finance-red/10 flex items-center justify-center">
            <DollarSign className="h-5 w-5 text-finance-red" />
          </div>
          <div>
            <p className="text-sm font-medium">Total em Atraso</p>
            <p className="text-xl font-bold text-finance-red">
              {formatCurrency(summary.total_overdue_amount)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 border rounded-lg">
          <div className="w-10 h-10 rounded-full bg-finance-amber/10 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-finance-amber" />
          </div>
          <div>
            <p className="text-sm font-medium">Lançamentos</p>
            <p className="text-xl font-bold">
              {summary.total_transactions}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 border rounded-lg">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">Clientes</p>
            <p className="text-xl font-bold">
              {summary.total_clients}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 p-4 border rounded-lg">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <Clock className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <p className="text-sm font-medium">Média de Atraso</p>
            <p className="text-xl font-bold">
              {Math.round(summary.average_days_overdue)} dias
            </p>
          </div>
        </div>
      </div>

      {/* Active Filters */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Filtros ativos:</span>
          
          {filters.client_search && (
            <Badge variant="secondary" className="text-xs">
              Busca: "{filters.client_search}"
            </Badge>
          )}
          
          {filters.priority && filters.priority.length !== Object.values(OverduePriority).length && (
            <Badge variant="secondary" className="text-xs">
              {filters.priority.length} prioridade(s)
            </Badge>
          )}
          
          {(filters.days_overdue_min !== undefined || filters.days_overdue_max !== undefined) && (
            <Badge variant="secondary" className="text-xs">
              {filters.days_overdue_min || 0}-{filters.days_overdue_max || 365} dias
            </Badge>
          )}
          
          {(filters.amount_min !== undefined || filters.amount_max !== undefined) && (
            <Badge variant="secondary" className="text-xs">
              Valor: {filters.amount_min ? formatCurrency(filters.amount_min) : 'min'} - {filters.amount_max ? formatCurrency(filters.amount_max) : 'max'}
            </Badge>
          )}
          
          {filters.categories?.length && (
            <Badge variant="secondary" className="text-xs">
              {filters.categories.length} categoria(s)
            </Badge>
          )}
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="text-xs h-6 px-2"
          >
            Limpar filtros
          </Button>
        </div>
      )}
    </div>
  );
}
